/**
 * admin/src/pages/FeedbackPage.tsx
 * Trang quản lý Khảo sát & Báo lỗi bài tập
 *
 * Fix: Dùng adminFetch() thay vì fetch() trực tiếp để URL đúng trên prod
 * Upgrade: SVG donut charts, trend bars, phân tích tổng quan
 */

import { useEffect, useState, useCallback } from "react";
import { adminFetch } from "../hooks/useAdmin";


// ── Types ──────────────────────────────────────────────────────────────────

interface FeedbackStats {
  ok: boolean;
  summary: {
    total: number;
    hard_count: number;
    dislike_count: number;
    unclear_count: number;
  } | null;
  byQuiz: Array<{
    quiz_id: string;
    quiz_title: string | null;
    total_responses: number;
    diff_easy: number; diff_medium: number; diff_hard: number;
    enj_love: number; enj_ok: number; enj_dislike: number;
    clar_clear: number; clar_unclear: number;
  }>;
}

interface ReportItem {
  id: string;
  quiz_id: string;
  quiz_title: string | null;
  report_type: string;
  message: string | null;
  status: string;
  stars_awarded: number;
  created_at: number;
  student_name: string | null;
}

interface ReportResponse {
  ok: boolean;
  results: ReportItem[];
  statusCounts: Record<string, number>;
}

// ── Constants ───────────────────────────────────────────────────────────────

const REPORT_TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  wrong_answer:  { label: "Đáp án sai",   icon: "❌", color: "#ef4444" },
  typo:          { label: "Lỗi chính tả", icon: "✏️", color: "#f59e0b" },
  unclear:       { label: "Khó hiểu",     icon: "❓", color: "#8b5cf6" },
  broken_image:  { label: "Hình bị lỗi",  icon: "🖼️", color: "#3b82f6" },
  other:         { label: "Khác",          icon: "📝", color: "#6b7280" },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending:   { label: "Chờ xử lý", color: "#d97706", bg: "#fef3c7" },
  resolved:  { label: "Đã xử lý",  color: "#16a34a", bg: "#dcfce7" },
  dismissed: { label: "Bỏ qua",    color: "#6b7280", bg: "#f3f4f6" },
};

// ── Utility ─────────────────────────────────────────────────────────────────

function pct(a: number, total: number) {
  if (!total) return 0;
  return Math.round((a / total) * 100);
}

function formatDate(ts: number) {
  return new Date(ts * 1000).toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── SVG Donut Chart ─────────────────────────────────────────────────────────

function DonutChart({
  segments,
  size = 80,
  label,
}: {
  segments: { value: number; color: string; name: string }[];
  size?: number;
  label?: string;
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (!total) {
    return (
      <div style={{ textAlign: "center", color: "var(--text-3)", fontSize: 12 }}>
        Chưa có dữ liệu
      </div>
    );
  }

  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  let offset = 0;
  const arcs = segments
    .filter(s => s.value > 0)
    .map(seg => {
      const ratio = seg.value / total;
      const dash = ratio * circumference;
      const gap = circumference - dash;
      const arc = { ...seg, dash, gap, offset };
      offset += dash;
      return arc;
    });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <svg width={size} height={size} style={{ flexShrink: 0, transform: "rotate(-90deg)" }}>
        {arcs.map((arc, i) => (
          <circle
            key={i}
            r={r}
            cx={cx}
            cy={cy}
            fill="none"
            stroke={arc.color}
            strokeWidth={10}
            strokeDasharray={`${arc.dash} ${arc.gap}`}
            strokeDashoffset={-arc.offset}
            strokeLinecap="butt"
          />
        ))}
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={13}
          fontWeight="700"
          fill="var(--text-1)"
          style={{ transform: "rotate(90deg)", transformOrigin: `${cx}px ${cy}px` }}
        >
          {label ?? total}
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {segments.filter(s => s.value > 0).map(seg => (
          <div key={seg.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
            <span style={{ color: "var(--text-2)" }}>{seg.name}</span>
            <span style={{ color: "var(--text-3)", marginLeft: "auto" }}>{pct(seg.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Horizontal Bar ──────────────────────────────────────────────────────────

function HBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const w = pct(value, total);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3, color: "var(--text-2)" }}>
        <span>{label}</span>
        <span style={{ color: "var(--text-3)" }}>{value} <span style={{ fontSize: 10 }}>({w}%)</span></span>
      </div>
      <div style={{ height: 6, background: "var(--bg-elevated)", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 99, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

// ── Summary Metric Card ──────────────────────────────────────────────────────

function MetricCard({ icon, value, label, sub, color }: {
  icon: string; value: number; label: string; sub?: string; color?: string;
}) {
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: color ? `${color}18` : "var(--bg-elevated)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: color ?? "var(--text-1)", lineHeight: 1.1 }}>
          {value.toLocaleString("vi-VN")}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)", marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Loading Skeleton ─────────────────────────────────────────────────────────

function SkeletonBlock({ height = 120 }: { height?: number }) {
  return (
    <div style={{
      height, borderRadius: "var(--radius-lg)",
      background: "var(--bg-elevated)",
      animation: "pulse 1.5s ease-in-out infinite",
    }} />
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export function FeedbackPage() {
  const [tab, setTab] = useState<"feedback" | "reports">("feedback");
  const [days, setDays] = useState(30);

  // Feedback state
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Reports state
  const [reportStatus, setReportStatus] = useState("pending");
  const [reports, setReports] = useState<ReportResponse | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // ── Load Feedback Stats via adminFetch (handles prod/dev URL automatically) ──
  const loadStats = useCallback(() => {
    if (tab !== "feedback") return;
    setStatsLoading(true);
    setStatsError(null);
    adminFetch<FeedbackStats>("/feedback/stats", { params: { days } })
      .then(data => {
        if (data.ok) setStats(data);
        else setStatsError("Server trả về lỗi");
      })
      .catch(err => setStatsError(err instanceof Error ? err.message : "Lỗi kết nối"))
      .finally(() => setStatsLoading(false));
  }, [tab, days]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Load Reports via adminFetch ──
  const loadReports = useCallback(() => {
    if (tab !== "reports") return;
    setReportsLoading(true);
    adminFetch<ReportResponse>("/reports", { params: { status: reportStatus, limit: 50 } })
      .then(data => { if (data.ok) setReports(data); })
      .catch(console.error)
      .finally(() => setReportsLoading(false));
  }, [tab, reportStatus]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // ── Update report status ──
  const updateReportStatus = async (id: string, newStatus: string) => {
    setUpdatingId(id);
    try {
      await adminFetch(`/reports/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setReports(prev => prev ? {
        ...prev,
        results: prev.results.map(r => r.id === id ? { ...r, status: newStatus } : r),
      } : null);
    } catch (e) { console.error(e); }
    finally { setUpdatingId(null); }
  };

  // ── Derived: top problematic quizzes ──
  const topHard = stats?.byQuiz
    .filter(q => q.diff_hard > 0)
    .sort((a, b) => b.diff_hard - a.diff_hard)
    .slice(0, 5) ?? [];

  const topDisliked = stats?.byQuiz
    .filter(q => q.enj_dislike > 0)
    .sort((a, b) => b.enj_dislike - a.enj_dislike)
    .slice(0, 5) ?? [];

  const pendingCount = reports?.statusCounts?.["pending"] ?? 0;

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* ── Header ── */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div className="page-header__left">
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>📊 Feedback & Báo lỗi bài tập</h1>
          <p style={{ margin: "4px 0 0", color: "var(--text-3)", fontSize: 13.5 }}>
            Phân tích cảm nhận học sinh · Xử lý báo cáo lỗi nội dung
          </p>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid var(--border)", paddingBottom: 0 }}>
        {(["feedback", "reports"] as const).map(t => (
          <button
            key={t}
            id={`tab-${t}`}
            onClick={() => setTab(t)}
            style={{
              padding: "10px 20px",
              border: "none",
              borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
              background: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              color: tab === t ? "var(--accent)" : "var(--text-3)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "all 0.15s",
              marginBottom: -1,
            }}
          >
            {t === "feedback" ? "📊 Thống kê khảo sát" : "🚩 Báo lỗi"}
            {t === "reports" && pendingCount > 0 && (
              <span style={{
                background: "#ef4444", color: "#fff",
                borderRadius: 99, padding: "1px 6px", fontSize: 11, fontWeight: 700,
              }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 1: FEEDBACK STATS                                   */}
      {/* ════════════════════════════════════════════════════════ */}
      {tab === "feedback" && (
        <div>
          {/* Bộ lọc thời gian */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
            <span style={{ fontSize: 13, color: "var(--text-3)", fontWeight: 500 }}>Xem trong:</span>
            {[7, 14, 30, 60].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                  padding: "6px 14px",
                  border: "1px solid",
                  borderColor: days === d ? "var(--accent)" : "var(--border)",
                  borderRadius: "var(--radius)",
                  background: days === d ? "var(--accent)" : "transparent",
                  color: days === d ? "#fff" : "var(--text-2)",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {d} ngày
              </button>
            ))}
            {statsLoading && <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: 8 }}>⏳ Đang tải…</span>}
            <button
              onClick={loadStats}
              style={{
                marginLeft: "auto", padding: "6px 14px", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", background: "transparent", cursor: "pointer",
                fontSize: 13, color: "var(--text-2)",
              }}
            >
              🔄 Làm mới
            </button>
          </div>

          {/* Error */}
          {statsError && (
            <div style={{
              padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "var(--radius-lg)", color: "#991b1b", fontSize: 13.5, marginBottom: 20,
            }}>
              ⚠️ {statsError}
            </div>
          )}

          {statsLoading ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              {[...Array(4)].map((_, i) => <SkeletonBlock key={i} height={100} />)}
            </div>
          ) : stats ? (
            <>
              {/* Summary Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
                <MetricCard icon="📝" value={stats.summary?.total ?? 0} label="Tổng khảo sát" sub={`trong ${days} ngày qua`} />
                <MetricCard icon="😓" value={stats.summary?.hard_count ?? 0} label="Thấy khó" color="#f59e0b"
                  sub={`${pct(stats.summary?.hard_count ?? 0, stats.summary?.total ?? 1)}% phản hồi`} />
                <MetricCard icon="😕" value={stats.summary?.dislike_count ?? 0} label="Không thích" color="#ef4444"
                  sub={`${pct(stats.summary?.dislike_count ?? 0, stats.summary?.total ?? 1)}% phản hồi`} />
                <MetricCard icon="❓" value={stats.summary?.unclear_count ?? 0} label="Khó hiểu" color="#8b5cf6"
                  sub={`${pct(stats.summary?.unclear_count ?? 0, stats.summary?.total ?? 1)}% phản hồi`} />
              </div>

              {/* Health overview donut + top lists */}
              {stats.summary && stats.summary.total > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
                  {/* Overall donut */}
                  <div className="card" style={{ padding: "20px 24px" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-3)", marginBottom: 16 }}>
                      Tổng quan sức khoẻ nội dung
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 8, fontWeight: 600 }}>🎯 Độ khó</div>
                        <DonutChart size={90} segments={[
                          { value: stats.byQuiz.reduce((s, q) => s + q.diff_easy, 0), color: "#22c55e", name: "Dễ" },
                          { value: stats.byQuiz.reduce((s, q) => s + q.diff_medium, 0), color: "#f59e0b", name: "Vừa" },
                          { value: stats.byQuiz.reduce((s, q) => s + q.diff_hard, 0), color: "#ef4444", name: "Khó" },
                        ]} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 8, fontWeight: 600 }}>❤️ Cảm xúc</div>
                        <DonutChart size={90} segments={[
                          { value: stats.byQuiz.reduce((s, q) => s + q.enj_love, 0), color: "#ec4899", name: "Thích" },
                          { value: stats.byQuiz.reduce((s, q) => s + q.enj_ok, 0), color: "#94a3b8", name: "Bình thường" },
                          { value: stats.byQuiz.reduce((s, q) => s + q.enj_dislike, 0), color: "#ef4444", name: "Không thích" },
                        ]} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 8, fontWeight: 600 }}>📖 Nội dung</div>
                        <DonutChart size={90} segments={[
                          { value: stats.byQuiz.reduce((s, q) => s + q.clar_clear, 0), color: "#22c55e", name: "Dễ hiểu" },
                          { value: stats.byQuiz.reduce((s, q) => s + q.clar_unclear, 0), color: "#ef4444", name: "Khó hiểu" },
                        ]} />
                      </div>
                    </div>
                  </div>

                  {/* Top problem quizzes */}
                  <div className="card" style={{ padding: "20px 24px" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-3)", marginBottom: 16 }}>
                      🔴 Bài cần chú ý nhất
                    </div>
                    {topHard.length === 0 && topDisliked.length === 0 ? (
                      <p style={{ color: "var(--text-3)", fontSize: 13 }}>Không có bài nào cần chú ý.</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(() => {
                          type QuizWithReason = typeof topHard[0] & { reason: string };
                          const seen = new Set<string>();
                          const combined: QuizWithReason[] = [];
                          for (const q of topHard) {
                            if (!seen.has(q.quiz_id)) { seen.add(q.quiz_id); combined.push({ ...q, reason: "😓 Nhiều học sinh thấy khó" }); }
                          }
                          for (const q of topDisliked) {
                            if (!seen.has(q.quiz_id)) { seen.add(q.quiz_id); combined.push({ ...q, reason: "😕 Không được yêu thích" }); }
                          }
                          return combined.slice(0, 5).map(q => (
                            <div key={q.quiz_id} style={{
                              padding: "10px 14px", background: "var(--bg-elevated)",
                              borderRadius: "var(--radius)", display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                              <div>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-1)" }}>
                                  {q.quiz_id}
                                </div>
                                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{q.reason}</div>
                              </div>
                              <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "right" }}>
                                {q.total_responses} phản hồi
                              </div>
                            </div>
                          ));
                        })()}

                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Per-quiz detail */}
              <div className="card" style={{ padding: "20px 24px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-3)", marginBottom: 16 }}>
                  Phân tích chi tiết theo bài tập ({stats.byQuiz.length} bài)
                </div>
                {stats.byQuiz.length === 0 ? (
                  <p style={{ color: "var(--text-3)", fontSize: 13, textAlign: "center", padding: "32px 0" }}>
                    Chưa có dữ liệu trong {days} ngày qua.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {stats.byQuiz.map(q => (
                      <div key={q.quiz_id} style={{
                        border: "1px solid var(--border)", borderRadius: "var(--radius-lg)",
                        padding: "16px 20px", background: "var(--bg-card)",
                      }}>
                        {/* Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                          <div>
                            <span style={{
                              fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
                              background: "var(--accent-dim, #eff6ff)", color: "var(--accent)",
                              padding: "2px 8px", borderRadius: 4,
                            }}>
                              {q.quiz_id}
                            </span>
                            {q.quiz_title && (
                              <span style={{ marginLeft: 8, fontSize: 13.5, fontWeight: 600, color: "var(--text-1)" }}>
                                {q.quiz_title}
                              </span>
                            )}
                          </div>
                          <span style={{
                            fontSize: 12, color: "var(--text-3)",
                            background: "var(--bg-elevated)",
                            padding: "3px 10px", borderRadius: 99,
                          }}>
                            {q.total_responses} phản hồi
                          </span>
                        </div>

                        {/* 3 horizontal bar groups */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
                          <div>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>🎯 Độ khó</div>
                            <HBar label="😄 Dễ"   value={q.diff_easy}   total={q.total_responses} color="#22c55e" />
                            <HBar label="😊 Vừa"  value={q.diff_medium} total={q.total_responses} color="#f59e0b" />
                            <HBar label="😓 Khó"  value={q.diff_hard}   total={q.total_responses} color="#ef4444" />
                          </div>
                          <div>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>❤️ Cảm xúc</div>
                            <HBar label="❤️ Thích"       value={q.enj_love}    total={q.total_responses} color="#ec4899" />
                            <HBar label="😐 Bình thường" value={q.enj_ok}      total={q.total_responses} color="#94a3b8" />
                            <HBar label="😕 Không thích" value={q.enj_dislike} total={q.total_responses} color="#ef4444" />
                          </div>
                          <div>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>📖 Rõ ràng</div>
                            <HBar label="👍 Dễ hiểu"  value={q.clar_clear}   total={q.total_responses} color="#22c55e" />
                            <HBar label="👎 Khó hiểu" value={q.clar_unclear} total={q.total_responses} color="#ef4444" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : !statsError ? (
            <p style={{ color: "var(--text-3)", textAlign: "center", padding: "48px 0" }}>Không có dữ liệu.</p>
          ) : null}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════ */}
      {/* TAB 2: REPORTS                                          */}
      {/* ════════════════════════════════════════════════════════ */}
      {tab === "reports" && (
        <div>
          {/* Status filter */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
            {["pending", "resolved", "dismissed", "all"].map(s => {
              const cfg = STATUS_CONFIG[s] ?? { label: "Tất cả", color: "var(--text-2)", bg: "var(--bg-elevated)" };
              const count = reports?.statusCounts?.[s];
              const active = reportStatus === s;
              return (
                <button
                  key={s}
                  id={`filter-${s}`}
                  onClick={() => setReportStatus(s)}
                  style={{
                    padding: "7px 16px",
                    border: "1px solid",
                    borderColor: active ? cfg.color : "var(--border)",
                    borderRadius: "var(--radius)",
                    background: active ? cfg.bg : "transparent",
                    color: active ? cfg.color : "var(--text-2)",
                    fontWeight: 600, fontSize: 13, cursor: "pointer",
                    transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  {s === "pending" ? "⏳" : s === "resolved" ? "✅" : s === "dismissed" ? "🚫" : "📋"}
                  {s === "pending" ? "Chờ xử lý" : s === "resolved" ? "Đã xử lý" : s === "dismissed" ? "Bỏ qua" : "Tất cả"}
                  {count !== undefined && (
                    <span style={{
                      background: active ? cfg.color : "var(--bg-elevated)",
                      color: active ? "#fff" : "var(--text-2)",
                      borderRadius: 99, padding: "0 6px", fontSize: 11,
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
            <button
              onClick={loadReports}
              style={{
                marginLeft: "auto", padding: "7px 14px", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", background: "transparent", cursor: "pointer",
                fontSize: 13, color: "var(--text-2)",
              }}
            >
              🔄 Làm mới
            </button>
          </div>

          {/* Report summary row */}
          {reports && (
            <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
              {Object.entries(reports.statusCounts).map(([status, count]) => {
                const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.dismissed;
                return (
                  <div key={status} style={{
                    padding: "8px 16px", borderRadius: "var(--radius)",
                    background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 13,
                    border: `1px solid ${cfg.color}30`,
                  }}>
                    {count} {cfg.label}
                  </div>
                );
              })}
            </div>
          )}

          {reportsLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[...Array(4)].map((_, i) => <SkeletonBlock key={i} height={90} />)}
            </div>
          ) : (reports?.results ?? []).length === 0 ? (
            <div style={{
              textAlign: "center", padding: "60px 0",
              color: "var(--text-3)", fontSize: 14,
            }}>
              ✅ Không có báo cáo nào ở trạng thái này.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {reports!.results.map(r => {
                const typeCfg = REPORT_TYPE_LABELS[r.report_type] ?? { label: r.report_type, icon: "📝", color: "#6b7280" };
                const statusCfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.dismissed;
                return (
                  <div
                    key={r.id}
                    style={{
                      background: "var(--bg-card)",
                      border: "1px solid var(--border)",
                      borderLeft: `3px solid ${typeCfg.color}`,
                      borderRadius: "var(--radius-lg)",
                      padding: "14px 18px",
                    }}
                  >
                    {/* Top row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          background: "var(--bg-elevated)", color: "var(--accent)",
                          padding: "2px 8px", borderRadius: 4, letterSpacing: "0.05em",
                        }}>
                          {r.quiz_id}
                        </span>
                        {r.quiz_title && (
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-1)" }}>
                            {r.quiz_title}
                          </span>
                        )}
                        <span style={{
                          fontSize: 12.5, fontWeight: 600,
                          background: `${typeCfg.color}15`, color: typeCfg.color,
                          padding: "2px 10px", borderRadius: 99,
                        }}>
                          {typeCfg.icon} {typeCfg.label}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: statusCfg.color, background: statusCfg.bg,
                        padding: "3px 10px", borderRadius: 99,
                        whiteSpace: "nowrap",
                      }}>
                        {statusCfg.label}
                      </span>
                    </div>

                    {/* Message */}
                    {r.message && (
                      <div style={{
                        margin: "10px 0", padding: "8px 12px",
                        background: "var(--bg-elevated)", borderRadius: "var(--radius)",
                        fontSize: 13, color: "var(--text-2)", fontStyle: "italic",
                      }}>
                        "{r.message}"
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                        👤 {r.student_name ?? "Ẩn danh"} · 🕐 {formatDate(r.created_at)}
                      </span>
                      {r.status === "pending" && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            id={`btn-resolve-${r.id.slice(0, 8)}`}
                            disabled={updatingId === r.id}
                            onClick={() => updateReportStatus(r.id, "resolved")}
                            style={{
                              padding: "5px 14px",
                              border: "1px solid #16a34a",
                              borderRadius: "var(--radius)",
                              background: "transparent", color: "#16a34a",
                              fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                              opacity: updatingId === r.id ? 0.5 : 1,
                            }}
                          >
                            ✅ Đã xử lý
                          </button>
                          <button
                            id={`btn-dismiss-${r.id.slice(0, 8)}`}
                            disabled={updatingId === r.id}
                            onClick={() => updateReportStatus(r.id, "dismissed")}
                            style={{
                              padding: "5px 14px",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius)",
                              background: "transparent", color: "var(--text-3)",
                              fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                              opacity: updatingId === r.id ? 0.5 : 1,
                            }}
                          >
                            🚫 Bỏ qua
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Inline keyframe for skeleton */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
